import React, { useEffect, useRef, useState, useCallback } from "react";
import { mediaUrl } from "../lib/api";
import { trackMediaPlay, trackMediaComplete } from "../lib/analytics";
import { Play, Pause, Volume2, VolumeX, Maximize, SkipBack, SkipForward } from "lucide-react";

function fmt(sec) {
  if (!Number.isFinite(sec) || sec < 0) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s < 10 ? "0" : ""}${s}`;
}

function useMedia(ref, kind) {
  const [state, setState] = useState({ playing: false, current: 0, duration: 0, volume: 1, muted: false });
  const playedRef = useRef(false);
  const completedRef = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onPlay = () => {
      setState((s) => ({ ...s, playing: true }));
      if (!playedRef.current) {
        playedRef.current = true;
        try { trackMediaPlay(kind, el.duration || null); } catch {}
      }
    };
    const onPause = () => setState((s) => ({ ...s, playing: false }));
    const onTime = () => setState((s) => ({ ...s, current: el.currentTime }));
    const onMeta = () => setState((s) => ({ ...s, duration: el.duration || 0 }));
    const onVol = () => setState((s) => ({ ...s, volume: el.volume, muted: el.muted }));
    const onEnded = () => {
      if (!completedRef.current) {
        completedRef.current = true;
        try { trackMediaComplete(kind, el.duration || null); } catch {}
      }
    };
    el.addEventListener("play", onPlay);
    el.addEventListener("pause", onPause);
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("loadedmetadata", onMeta);
    el.addEventListener("durationchange", onMeta);
    el.addEventListener("volumechange", onVol);
    el.addEventListener("ended", onEnded);
    return () => {
      el.removeEventListener("play", onPlay);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("loadedmetadata", onMeta);
      el.removeEventListener("durationchange", onMeta);
      el.removeEventListener("volumechange", onVol);
      el.removeEventListener("ended", onEnded);
    };
  }, [ref, kind]);
  return state;
}

function ControlBar({ mediaRef, state, canFullscreen, onFullscreen }) {
  const togglePlay = useCallback(() => {
    const el = mediaRef.current; if (!el) return;
    if (el.paused) el.play(); else el.pause();
  }, [mediaRef]);
  const seek = useCallback((pct) => {
    const el = mediaRef.current;
    if (!el || !state.duration) return;
    el.currentTime = pct * state.duration;
  }, [mediaRef, state.duration]);
  const skip = (sec) => { const el = mediaRef.current; if (el) el.currentTime = Math.max(0, Math.min((state.duration || 0), el.currentTime + sec)); };
  const setVol = (v) => { const el = mediaRef.current; if (el) { el.volume = v; el.muted = v === 0; } };
  const toggleMute = () => { const el = mediaRef.current; if (el) { el.muted = !el.muted; } };

  const pct = state.duration > 0 ? state.current / state.duration : 0;

  const onSeekClick = (e) => {
    const bar = e.currentTarget;
    const rect = bar.getBoundingClientRect();
    seek(Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)));
  };

  return (
    <div className="xs-media-controls" data-testid="media-controls">
      <button className="xs-mc-btn" onClick={togglePlay} data-testid="media-play" aria-label={state.playing ? "Pause" : "Play"}>
        {state.playing ? <Pause size={18} /> : <Play size={18} />}
      </button>
      <button className="xs-mc-btn" onClick={() => skip(-10)} aria-label="Back 10s"><SkipBack size={16} /></button>
      <button className="xs-mc-btn" onClick={() => skip(10)} aria-label="Forward 10s"><SkipForward size={16} /></button>
      <span className="xs-mc-time">{fmt(state.current)}</span>
      <div className="xs-mc-progress" onClick={onSeekClick} data-testid="media-progress" role="slider" aria-valuenow={Math.round(pct * 100)}>
        <div className="xs-mc-progress-fill" style={{ width: `${pct * 100}%` }} />
      </div>
      <span className="xs-mc-time">{fmt(state.duration)}</span>
      <button className="xs-mc-btn" onClick={toggleMute} aria-label={state.muted ? "Unmute" : "Mute"}>
        {state.muted || state.volume === 0 ? <VolumeX size={16} /> : <Volume2 size={16} />}
      </button>
      <input
        type="range" min="0" max="1" step="0.05"
        value={state.muted ? 0 : state.volume}
        onChange={(e) => setVol(Number(e.target.value))}
        className="xs-mc-volume"
        aria-label="Volume"
      />
      {canFullscreen && (
        <button className="xs-mc-btn" onClick={onFullscreen} aria-label="Fullscreen"><Maximize size={16} /></button>
      )}
    </div>
  );
}

export function AudioPlayer({ src, title }) {
  const ref = useRef(null);
  const state = useMedia(ref, "audio");
  if (!src) return null;
  return (
    <div className="xs-media-wrap" data-testid="audio-player">
      {title && <div className="xs-media-title">♪ {title}</div>}
      <audio ref={ref} src={mediaUrl(src)} preload="metadata" />
      <ControlBar mediaRef={ref} state={state} />
    </div>
  );
}

export function VideoPlayer({ src, poster, title }) {
  const ref = useRef(null);
  const wrapRef = useRef(null);
  const state = useMedia(ref, "video");
  const onFullscreen = () => {
    const el = wrapRef.current;
    if (!el) return;
    if (document.fullscreenElement) document.exitFullscreen();
    else el.requestFullscreen?.();
  };
  if (!src) return null;
  return (
    <div className="xs-media-wrap xs-media-video" ref={wrapRef} data-testid="video-player">
      {title && <div className="xs-media-title">▶ {title}</div>}
      <video
        ref={ref}
        src={mediaUrl(src)}
        poster={poster ? mediaUrl(poster) : undefined}
        preload="metadata"
        playsInline
        onClick={() => { const el = ref.current; if (!el) return; el.paused ? el.play() : el.pause(); }}
      />
      <ControlBar mediaRef={ref} state={state} canFullscreen onFullscreen={onFullscreen} />
    </div>
  );
}
